<template>
  <ResponsiveLayout
    sticky-actions
    :progress="progress"
    :show-lang-picker="false"
  >
    <template #header_start>
      <ButtonBack @click="emit('back')" />
    </template>
    <template #header_middle>
      <div
        class="kds-typography-title-medium"
        v-html="$t('b2b_self_test_generic_assessment')"
        v-if="showHeaderTitle"
      />
    </template>
    <div class="kds-main-wrapper">
      <h3
        class="kds-typography-title-xlarge"
        v-html="$t('b2b_self_test_pain_duration_question')"
      />
      <div class="self-test">
        <fieldset class="form-container">
          <div
            v-for="option in painDurationOptions"
            :key="option.value"
            :class="[
              'quiz-radio',
              { 'quiz-radio--checked': chronificationLevel === option.value },
            ]"
            @click="handleChronificationLevelChange(option)"
          >
            <label class="quiz-radio__label kds-typography-title-medium">
              {{ $t(option.label) }}
            </label>
            <div class="quiz-radio__layout">
              <custom-radio
                :checked="chronificationLevel === option.value"
                :data-qa="'pain-duration-' + option.value"
                :value="option.value"
              />
            </div>
          </div>
        </fieldset>
      </div>
    </div>
    <template #actions>
      <BaseButton
        :disabled="!isValid"
        :text="$t('generic_action_continue')"
        data-qa="submit"
        @click="next"
      />
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import useSelfTestStore from '@shared/modules/assessment/stores/useSelfTestStore.js'
import CustomRadio from '@shared/components/CustomRadio.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import env from '@shared/env'
import { computed, ref } from 'vue'
import { useI18n } from 'petite-vue-i18n'
import { isFromApp } from '@shared/helpers/isFromApp'
import usePreSubscriptionWebOptimizedFunnel from '@shared/composables/usePreSubscriptionWebOptimizedFunnel'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import { preloadImage } from '@shared/utils/preloadImage'
import {
  PAIN_DURATION_OPTIONS_MSK_EU,
  PAIN_DURATION_OPTIONS_OTHER,
} from '@shared/modules/assessment/config/selfTestOptions'

const painDurationOptions = env.isMskEu
  ? PAIN_DURATION_OPTIONS_MSK_EU
  : PAIN_DURATION_OPTIONS_OTHER

const props = defineProps({
  progress: {
    type: Number,
    default: null,
  },
  handleNextEvent: {
    type: Function,
    default: () => {},
  },
})

const emit = defineEmits(['back'])

const selfTestStore = useSelfTestStore()
const { t } = useI18n()

const chronificationLevel = ref(selfTestStore.chronificationLevel)

const preSubscriptionWebOptimizedFunnel = usePreSubscriptionWebOptimizedFunnel()

const showHeaderTitle = computed(
  () => !preSubscriptionWebOptimizedFunnel.value && isFromApp(),
)

const handleChronificationLevelChange = (option) => {
  chronificationLevel.value = option.value
  preloadImage(option.image)
}

const isValid = computed(() => chronificationLevel.value != null)

const next = () => {
  const option = painDurationOptions.find(
    (o) => o.value === chronificationLevel.value,
  )
  if (!option) return
  selfTestStore.answer('chronification_level', {
    value: option.value,
    text: t(option.label),
    questionText: t('b2b_self_test_pain_duration_question'),
  })
  props.handleNextEvent()
}
</script>

<style lang="scss">
@use '@shared/modules/assessment/views/css/SelfTest.scss' as *;
</style>
